import type { ReactNode } from "react";

/**
 * The page frame for this fork's screens: the inset the mobile layout does not
 * supply, and nothing on desktop, where the layout already does.
 *
 * WHY THIS EXISTS
 * ---------------
 * Reported from production with a screenshot of /contacts/outbound on a phone:
 * the `<h1>` and the prose under it ran edge to edge while upstream's Settings,
 * Tasks and Contacts screens had a comfortable margin. There are two layouts
 * and only ONE of them supplies a column:
 *
 *   layout/Layout.tsx:15   <main className="max-w-screen-xl mx-auto pt-4 px-4">
 *   layout/MobileLayout.tsx   ErrorBoundary > Suspense > children, then the
 *                             bar. No <main>, no padding, no bottom inset.
 *
 * Upstream compensates PER SCREEN with layout/MobileContent.tsx at eight call
 * sites, because upstream's pattern is a SEPARATE mobile component per screen
 * (MobileTasksList, SettingsPageMobile, ContactListMobile...). This fork's
 * screens are single components serving both layouts, so they never adopted
 * it, and mobile.tsx said so in a comment that was half right: "layout/
 * Layout.tsx already gives every page a column" -- true on desktop, and on
 * mobile there was no column at all. Measured before this component existed,
 * at 320x700 with the app's own stylesheet: h1.left = 0.0, h1.right = 320.0.
 *
 * ONE COMPONENT, APPLIED TO THE ROUTE ARRAY. Before this, five fork screens
 * had three different answers -- `pb-8`, `pb-8 space-y-6`, `p-4 pb-8
 * max-w-4xl`, `p-4 space-y-3`, and nothing at all on the rest -- and the two
 * that looked right on a phone looked right because somebody had hand-written
 * `p-4`. Five class strings is how three answers happened; it would drift
 * again. See mobile.tsx for why the frame is applied by mapping the routes
 * rather than at each call site.
 *
 * WHY NOT layout/MobileContent.tsx, WHICH IS ALREADY THERE
 * --------------------------------------------------------
 * Its horizontal and bottom values are exactly these, and that is not a
 * coincidence -- they are copied, so the fork matches the app. What does not
 * carry across is `pt-18`. That 76.5px clears upstream's FIXED MobileHeader,
 * which this fork's screens do not render; adopting it would open every one of
 * them with 76px of empty space and nothing in it. `min-h-screen
 * overflow-y-auto` does not carry across either: it makes the screen its own
 * scroll container, which these screens do not want and which would put a
 * second scrollbar inside the page's.
 *
 * WHY NOT A GLOBAL INSET IN MobileLayout.tsx
 * ------------------------------------------
 * Upstream's eight mobile screens supply their own through MobileContent, so a
 * layout-level inset would draw 34px on every one of them. The frame has to
 * belong to the screens that lack one.
 *
 * WHY `max-md:` AND NOT `useIsMobile()`
 * -------------------------------------
 * Both mechanisms are available here and the requirement is the same either
 * way: the breakpoint that adds the inset must be the breakpoint that chooses
 * the layout, or there is a band of widths with no inset or a doubled one.
 *
 *   root/CRM.tsx:224     const ResponsiveAdmin = isMobile ? MobileAdmin : DesktopAdmin
 *   hooks/use-mobile.ts  window.innerWidth < 768
 *   max-md:              @media (width < 48rem)
 *
 * `48rem` is 768px and NOT 816px: a relative unit inside a media query
 * resolves against the INITIAL font-size, never against the `html { font-size:
 * 17px }` that index.css:145 sets below 768. Asserted rather than believed --
 * screenInset.test.tsx measures at 767 and at 768 and checks the bar and the
 * inset agree at both.
 *
 * CSS wins over the hook because `useIsMobile` starts `false` and corrects
 * itself in an effect, so a JS frame paints one desktop-width frame inside the
 * mobile layout on every route mount. A media query is right on the first
 * paint and needs no JavaScript at all.
 *
 * WHY DESKTOP KEEPS `pb-8`
 * ------------------------
 * It is not a second inset -- Layout.tsx supplies no bottom padding, so this
 * is the breathing room the screens were each writing for themselves, moved to
 * the one place. `max-md:pb-20` replaces it below the breakpoint, where the
 * 61.31px bottom bar sits over the content: `pb-8` is 34px at the 17px mobile
 * root font size, which was 27px SHORT of the bar (atomic#17).
 */
export const ScreenFrame = ({ children }: { children: ReactNode }) => (
  <div
    data-slot="screen-frame"
    className="pb-8 max-md:px-4 max-md:pt-4 max-md:pb-20"
  >
    {children}
  </div>
);
